"use client";

import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "../lib/utils";
import { SafeArea } from "capacitor-plugin-safe-area";

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => {
  const [safeArea, setSafeArea] = React.useState<any>({ top: 0 });
  React.useEffect(() => {
    SafeArea.getSafeAreaInsets().then((data) => {
      setSafeArea(data.insets);
    });
  }, []);

  return (
    <ToastPrimitives.Viewport
      ref={ref}
      className={cn(
        "z-40 fixed top-[0] flex w-[100vw] items-center justify-center p-4 md:max-w-[420px]",
        className,
      )}
      style={{
        paddingTop: `${(safeArea.top || 8) + 8}px`,
      }}
      {...props}
    />
  );
});
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-2xl border p-6 pr-8 backdrop-blur-2xl transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
  {
    variants: {
      variant: {
        default: "border-[hsla(220,20%,40%,0.2)] bg-[linear-gradient(145deg,hsla(220,20%,15%,0.9),hsla(220,20%,12%,0.95))] text-[hsl(220,10%,98%)] shadow-[0_8px_32px_0_hsla(220,20%,5%,0.4),inset_0_1px_0_hsla(220,20%,40%,0.2)]",
        destructive:
          "destructive group border-[hsla(0,70%,50%,0.3)] bg-[linear-gradient(145deg,hsla(0,70%,30%,0.9),hsla(0,70%,25%,0.95))] text-[hsl(0,0%,100%)] shadow-[0_8px_32px_0_hsla(0,70%,20%,0.5),inset_0_1px_0_hsla(0,70%,50%,0.3)]",
        error: "border-[hsla(0,85%,55%,0.3)] bg-[linear-gradient(145deg,hsla(0,85%,35%,0.9),hsla(0,85%,30%,0.95))] text-[hsl(0,0%,100%)] shadow-[0_8px_32px_0_hsla(0,85%,25%,0.5),inset_0_1px_0_hsla(0,85%,55%,0.3)]",
        success: "border-[hsla(195,85%,55%,0.3)] bg-[linear-gradient(145deg,hsla(195,85%,35%,0.9),hsla(195,85%,30%,0.95))] text-[hsl(0,0%,100%)] shadow-[0_8px_32px_0_hsla(195,85%,25%,0.5),inset_0_1px_0_hsla(195,85%,55%,0.3)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive",
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-2 top-2 rounded-lg p-1.5 text-[hsl(220,10%,98%)]/60 opacity-0 transition-all hover:text-[hsl(220,10%,98%)] hover:bg-[hsla(220,20%,40%,0.2)] focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-[hsl(270,70%,60%)] focus:ring-offset-2 focus:ring-offset-transparent group-hover:opacity-100 group-[.destructive]:text-[hsl(0,0%,100%)]/70 group-[.destructive]:hover:text-[hsl(0,0%,100%)] group-[.destructive]:hover:bg-[hsla(0,70%,50%,0.2)] group-[.destructive]:focus:ring-[hsl(0,70%,50%)]",
      className,
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("text-sm font-semibold", className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("text-sm opacity-90", className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
